/**
 * The sign-in form's two small DOM behaviours, pulled out of index.js (which
 * imports Leaflet and CSS and so cannot be loaded by plain Node) so they have
 * a direct test: scripts/test-p1-auth-form.mjs.
 *
 * QA pass 5, item 5 ("Immediately after sign-out, the enabled button read
 * Signing in instead of Log In. Reloading corrected it") and item 1 ("After
 * signing out and reloading, the login form repopulated the account email
 * and a masked password").
 */

export const AUTH_BUTTON_IDS = ['signIn', 'signUp', 'signInGoogle'];

/**
 * Disable the auth buttons in flight (a slow request invites repeated clicks,
 * and each signup burns one of the few emails per hour the account is allowed),
 * and put every one of them back exactly as it was.
 *
 * Root cause of the stuck "Signing in" label, found by reading both writers of
 * the same node: premium-product.js's capture-phase click listener runs FIRST
 * and replaces the clicked button's content with a spinner + "Signing in",
 * stashing the real label in data-premium-label. The click handler then calls
 * this function, which used to remember `b.textContent` -- by then already the
 * spinner's "Signing in" -- as the label to restore. Sign-in succeeded, the
 * panel hid, and when it came back after sign-out the button was enabled but
 * permanently said "Signing in". The label to remember is therefore the one
 * premium-product.js saved, if it saved one; and restoring also clears that
 * script's loading state so its own timer cannot swap a stale label back in.
 */
export function setBusy(getEl, on, activeBtn, busyLabel) {
  for (const id of AUTH_BUTTON_IDS) {
    const b = getEl(id);
    if (!b) continue;
    if (on) {
      if (!b.dataset.label) b.dataset.label = b.dataset.premiumLabel || b.textContent;
      b.disabled = true;
      if (b === activeBtn && busyLabel) b.textContent = busyLabel;
    } else {
      b.disabled = false;
      if (b.dataset.label) { b.textContent = b.dataset.label; delete b.dataset.label; }
      b.classList?.remove('is-loading');
      b.removeAttribute?.('aria-busy');
      delete b.dataset.premiumLabel;
    }
  }
}

/**
 * Nothing the person typed to sign in may outlive the sign-in. The form fields
 * were never emptied: after a successful sign-in (and again after sign-out) the
 * account email and the password sat in the DOM, so a reload could restore the
 * email into the form the browser saved, and the password stayed readable to
 * anything on the page. Called when a session is applied and when it is ended.
 */
export function clearCredentialFields(getEl) {
  for (const id of ['email', 'password']) {
    const f = getEl(id);
    if (f && f.value) f.value = '';
  }
}
